import React, { useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';

import { Form, ModalProps, Typography } from 'antd';
import styled from 'styled-components';

import { createShopperEmail, updateShopperEmail } from 'apis/ShopRequestAPI';
import { useRootContext } from 'components/layout/RootContext';
import Button from 'components/lib/Button';
import Input from 'components/lib/Input';
import Modal from 'components/lib/Modal';
import PhoneInput, { PHONE_INPUT_VALIDATION_RULES } from 'components/lib/PhoneInput';
import { getErrorMessageByPriority, normalizeEmail, validateEmail } from 'helpers/emails';
import { normalizeNorthAmericanPhone } from 'helpers/phoneNumbers';

import { ShopperEmailType } from '../types';

const StyledModal = styled(Modal)`
  &&& {
    .ant-modal-body {
      min-height: 152px;
      display: flex;
      flex-direction: column;
    }
  }
`;

const Description = styled(Typography.Text)`
  display: block;
  padding-top: 18px;
  color: var(--text-secondary);
`;

const FormContainer = styled.div`
  padding: 22px 0 4px;
  min-height: 76px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-form-item-explain,
  .ant-form-item-extra {
    min-height: 22px;
  }
`;

const PhoneFormItem = styled(Form.Item)`
  && {
    min-height: 58px;
    margin-bottom: 8px;
  }
`;

type Props = {
  open: boolean;
  item?: ShopperEmailType;
  phoneRequired?: boolean;
  singleUse?: boolean;
  getContainer?: ModalProps['getContainer'];
  onCancel: () => void;
  onSaved?: (shopperEmail: ShopperEmailType) => void | Promise<void>;
};

type ShopperContactFormValues = {
  name: string;
  email: string | null;
  phone: string;
};

const ShopperEmailsEntryModal = ({
  open,
  item,
  phoneRequired = false,
  singleUse = false,
  getContainer,
  onCancel,
  onSaved,
}: Props) => {
  const { showMessage } = useRootContext();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<ShopperContactFormValues>();

  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }

    form.setFieldsValue({
      name: item?.name ?? '',
      email: item?.email ?? '',
      phone: item?.phone ?? '',
    });
  }, [form, item?.email, item?.name, item?.phone, open]);

  const { mutateAsync: createShopperEmailMutation, isLoading: isCreating } = useMutation({
    mutationFn: (payload: ShopperContactFormValues) => createShopperEmail(payload),
    onSuccess: () => {
      queryClient.invalidateQueries(['shopper-emails']);
    },
  });

  const { mutateAsync: updateShopperEmailMutation, isLoading: isUpdating } = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: ShopperContactFormValues }) => updateShopperEmail(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries(['shopper-emails']);
    },
  });

  const handleSubmit = React.useCallback(
    async (values: ShopperContactFormValues) => {
      const normalizedEmail = normalizeEmail(values.email);
      const payload = {
        name: values.name?.trim() ?? '',
        email: normalizedEmail || null,
        phone: normalizeNorthAmericanPhone(values.phone),
      };

      try {
        let savedShopperEmail: ShopperEmailType;

        if (item?.id) {
          savedShopperEmail = await updateShopperEmailMutation({ id: item.id, payload });
        } else {
          savedShopperEmail = await createShopperEmailMutation(payload);
        }

        await onSaved?.(savedShopperEmail);
        if (!onSaved) {
          showMessage(
            'success',
            item?.id ? 'Shopper contact updated successfully.' : 'Shopper contact created successfully.'
          );
        }
        onCancel();
      } catch (error: any) {
        showMessage(
          'error',
          getErrorMessageByPriority(error?.response?.data, 'Failed to save shopper contact.', [
            'name',
            'email',
            'phone',
            'non_field_errors',
            'detail',
          ])
        );
      }
    },
    [createShopperEmailMutation, item?.id, onCancel, onSaved, showMessage, updateShopperEmailMutation]
  );

  const isLoading = isCreating || isUpdating;
  const phoneRules = [
    ...(phoneRequired ? [{ required: true, message: 'Please enter a phone number' }] : []),
    ...PHONE_INPUT_VALIDATION_RULES,
  ];
  let description = 'Create a reusable shopper contact record that can be linked to multiple shop requests.';
  if (item) {
    description = 'Update the global shopper contact record. This affects every linked shop request.';
  } else if (singleUse) {
    description =
      'Create a shopper contact that will become permanently reserved when linked to this Speed to Lead request.';
  }

  return (
    <StyledModal
      open={open}
      getContainer={getContainer}
      title={item ? 'Edit Shopper Contact' : 'Add Shopper Contact'}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={isLoading} onClick={() => form.submit()}>
          Save
        </Button>,
      ]}
    >
      <Description>{description}</Description>
      <Form<ShopperContactFormValues>
        colon={false}
        form={form}
        initialValues={{ name: '', email: '', phone: '' }}
        labelCol={{ flex: '52px' }}
        onFinish={handleSubmit}
      >
        <FormContainer>
          <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Please enter a contact name' }]}>
            <Input placeholder="Enter contact name" />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            dependencies={['phone']}
            rules={[
              {
                validator: async (_, value) => {
                  const normalizedEmail = normalizeEmail(value);
                  const normalizedPhone = normalizeNorthAmericanPhone(form.getFieldValue('phone'));

                  if (!normalizedEmail && !normalizedPhone) {
                    throw new Error('Enter an email address or phone number');
                  }

                  if (singleUse && !normalizedEmail) {
                    throw new Error('Please enter an email address');
                  }

                  if (normalizedEmail && !validateEmail(normalizedEmail)) {
                    throw new Error('Please enter a valid email address');
                  }
                },
              },
            ]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>
          <PhoneFormItem
            label="Phone"
            name="phone"
            dependencies={['email']}
            required={phoneRequired}
            rules={phoneRules}
          >
            <PhoneInput />
          </PhoneFormItem>
        </FormContainer>
      </Form>
    </StyledModal>
  );
};

export default ShopperEmailsEntryModal;
